import { useMemo, useState } from "react";
import {
  Loader2, AlertCircle, RefreshCw, Search, Download, Plus, X, Check,
  GraduationCap, BadgeCheck, UserCheck, UserPlus, Building2, Trash2, Link2Off,
} from "lucide-react";
import { BASE_URL } from "../../../features/api/apiSlice";
import {
  useGetQsInstitutionsQuery,
  useCreateQsInstitutionMutation,
  useUpdateQsInstitutionMutation,
  useDeleteQsInstitutionMutation,
  useLazyGetHodCandidatesQuery,
  useLinkHodMutation,
  useUnlinkHodMutation,
  qsParams,
  type QsFilters,
  type QsInstitution,
  type HodCandidate,
} from "../api/qsInstitutionsApi";

/**
 * The QSRBN register of institutions teaching Quantity Surveying.
 *
 * Read for three different jobs, which is what the filters are shaped around:
 * who teaches QS in a given state, whose accreditation needs attention this
 * cycle, and which Heads of Department the Institute can actually reach.
 *
 * The HOD panel is the one that does something beyond the register: linking a
 * Head of Department to a member grants that member the `hod` role. It is
 * kept visually apart for that reason, and sits behind its own permission on
 * the API.
 */

const selectCls =
  "rounded-lg border border-slate-300 px-2.5 py-2 text-sm text-slate-700 bg-white " +
  "focus:outline-none focus:ring-2 focus:ring-indigo-200";
const inputCls =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 bg-white " +
  "focus:outline-none focus:ring-2 focus:ring-indigo-200";

function Tile({ label, value, tone = "slate" }: { label: string; value: number | string; tone?: string }) {
  const tones: Record<string, string> = {
    slate: "bg-slate-50 text-slate-600 border-slate-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${tones[tone] ?? tones.slate}`}>
      <div className="text-[10px] font-black uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-black mt-1">{value}</div>
    </div>
  );
}

function StatusBadge({ status, label }: { status: string; label: string }) {
  const tone: Record<string, string> = {
    approved: "text-emerald-700 bg-emerald-50 border-emerald-200",
    accreditation_ongoing: "text-sky-700 bg-sky-50 border-sky-200",
    re_accreditation_ongoing: "text-sky-700 bg-sky-50 border-sky-200",
    ready_for_re_accreditation: "text-amber-700 bg-amber-50 border-amber-200",
    provisional: "text-violet-700 bg-violet-50 border-violet-200",
    not_accredited: "text-rose-700 bg-rose-50 border-rose-200",
    newly_established: "text-slate-600 bg-slate-100 border-slate-200",
    unknown: "text-slate-400 bg-slate-50 border-slate-200",
  };
  return (
    <span className={`text-[11px] font-bold rounded px-1.5 py-0.5 border whitespace-nowrap ${tone[status] ?? tone.unknown}`}>
      {label}
    </span>
  );
}

export default function QsInstitutionsPage() {
  const [filters, setFilters] = useState<QsFilters>({ page: 1, per_page: 50 });
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<QsInstitution | "new" | null>(null);
  const [hodFor, setHodFor] = useState<QsInstitution | null>(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const { data, isLoading, isFetching, refetch } = useGetQsInstitutionsQuery(filters);

  const rows = data?.data ?? [];
  const meta = data?.meta ?? ({} as any);
  const pagination = data?.pagination;
  const totals = meta?.totals ?? {};
  const options = meta?.options ?? {};

  const setFilter = (patch: Partial<QsFilters>) =>
    setFilters((f) => ({ ...f, ...patch, page: 1 }));

  const active = useMemo(
    () => Boolean(filters.q || filters.state_id || filters.type || filters.status
      || filters.programme || filters.hod || filters.active),
    [filters],
  );

  const download = async () => {
    setExporting(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const qs = new URLSearchParams(
        qsParams({ ...filters, page: undefined, per_page: undefined }) as any,
      ).toString();
      const res = await fetch(`${BASE_URL}/admin/qs-institutions/export?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Export failed (HTTP ${res.status}).`);
      const url = window.URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = "niqs-qs-institutions.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.message || "Could not download the register.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-5 bg-white rounded-lg shadow-sm">

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800">QS Institutions</h2>
          <p className="text-xs text-gray-500 font-medium mt-1 max-w-2xl">
            The statutory register of institutions offering Quantity Surveying tuition in Nigeria —
            their accreditation, their Head of Department and their departmental contact.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={download}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Export
          </button>
          <button
            onClick={() => setEditing("new")}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700"
          >
            <Plus className="w-4 h-4" /> Add institution
          </button>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="p-2.5 rounded-lg border text-gray-500 hover:bg-gray-50 disabled:opacity-40"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="Institutions" value={totals.institutions ?? 0} tone="indigo" />
        <Tile label="Fully accredited" value={totals.approved ?? 0} tone="emerald" />
        <Tile label="HOD matched to a member" value={totals.hod_linked ?? 0} />
        <Tile
          label="HOD named, not matched"
          value={totals.hod_unlinked ?? 0}
          tone={(totals.hod_unlinked ?? 0) > 0 ? "amber" : "slate"}
        />
      </div>

      {/* ── Filters ─────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
            placeholder="Institution, acronym, HOD or membership no.…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") setFilter({ q: search.trim() }); }}
            onBlur={() => setFilter({ q: search.trim() })}
          />
        </div>

        <select className={selectCls} value={String(filters.state_id ?? "")}
                onChange={(e) => setFilter({ state_id: e.target.value })}>
          <option value="">All states</option>
          {(options.states ?? []).map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        <select className={selectCls} value={String(filters.type ?? "")}
                onChange={(e) => setFilter({ type: e.target.value })}>
          <option value="">All types</option>
          {Object.entries(options.types ?? {}).map(([v, l]) => <option key={v} value={v}>{l as string}</option>)}
        </select>

        <select className={selectCls} value={String(filters.status ?? "")}
                onChange={(e) => setFilter({ status: e.target.value })}>
          <option value="">Any accreditation</option>
          {Object.entries(options.statuses ?? {}).map(([v, l]) => <option key={v} value={v}>{l as string}</option>)}
        </select>

        <select className={selectCls} value={String(filters.programme ?? "")}
                onChange={(e) => setFilter({ programme: e.target.value })}>
          <option value="">Any programme</option>
          {Object.entries(options.programmes ?? {}).map(([v, l]) => <option key={v} value={v}>{l as string}</option>)}
        </select>

        <select className={selectCls} value={String(filters.hod ?? "")}
                onChange={(e) => setFilter({ hod: e.target.value as any })}>
          <option value="">HOD: any</option>
          <option value="unlinked">HOD: named, not matched</option>
          <option value="linked">HOD: matched to a member</option>
          <option value="none">HOD: none on file</option>
        </select>

        {active && (
          <button
            onClick={() => { setSearch(""); setFilters({ page: 1, per_page: filters.per_page }); }}
            className="text-xs text-slate-500 underline px-2"
          >
            Clear filters
          </button>
        )}
        <span className="text-xs text-slate-500 ml-auto">
          {meta.filtered_total ?? 0} shown
        </span>
      </div>

      {/* ── Table ───────────────────────────────────────────────────── */}
      <div className="overflow-x-auto border rounded-xl">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2.5 font-black">Institution</th>
              <th className="text-left px-3 py-2.5 font-black">State</th>
              <th className="text-left px-3 py-2.5 font-black">Type</th>
              <th className="text-left px-3 py-2.5 font-black">Programmes</th>
              <th className="text-left px-3 py-2.5 font-black">Accreditation</th>
              <th className="text-left px-3 py-2.5 font-black">Head of Department</th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading && (
              <tr><td colSpan={7} className="px-3 py-10 text-center">
                <Loader2 className="w-5 h-5 text-indigo-400 animate-spin mx-auto" />
              </td></tr>
            )}

            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-sm text-slate-400">
                {active ? "No institution matches these filters." : "The register is empty — run the register seeder."}
              </td></tr>
            )}

            {rows.map((i) => (
              <tr key={i.id} className={`hover:bg-slate-50 ${i.is_active ? "" : "opacity-50"}`}>
                <td className="px-3 py-2.5">
                  <div className="font-semibold text-slate-800">{i.name}</div>
                  {i.acronym && <div className="text-xs text-slate-400">{i.acronym}</div>}
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">{i.state ?? "—"}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">{i.type_label}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600">{i.programmes_label || "—"}</td>
                <td className="px-3 py-2.5">
                  <StatusBadge status={i.accreditation_status} label={i.status_label} />
                  {i.accreditation_note && (
                    <div className="text-[11px] text-slate-400 mt-0.5 max-w-[240px] truncate"
                         title={i.accreditation_note}>
                      {i.accreditation_note}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {i.hod_name ? (
                    <>
                      <div className="text-xs font-semibold text-slate-700">{i.hod_name}</div>
                      <div className="text-[11px] flex items-center gap-1 mt-0.5">
                        {i.hod_linked ? (
                          <span className="text-emerald-700 flex items-center gap-1">
                            <UserCheck className="w-3 h-3" /> {i.hod_user?.name}
                          </span>
                        ) : (
                          <span className="text-amber-600">not matched to a member</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <span className="text-xs text-slate-300">none on file</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  <button
                    onClick={() => setHodFor(i)}
                    className="inline-flex p-1.5 rounded text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"
                    title="Head of Department"
                  >
                    <UserPlus className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setEditing(i)}
                    className="inline-flex p-1.5 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"
                    title="Edit"
                  >
                    <Building2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination && pagination.last_page > 1 && (
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>Page {pagination.current_page} of {pagination.last_page} · {pagination.total} total</span>
          <div className="flex gap-2">
            <button disabled={pagination.current_page <= 1}
                    onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) - 1 }))}
                    className="px-3 py-1.5 rounded border disabled:opacity-40 hover:bg-slate-50">Previous</button>
            <button disabled={pagination.current_page >= pagination.last_page}
                    onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 1) + 1 }))}
                    className="px-3 py-1.5 rounded border disabled:opacity-40 hover:bg-slate-50">Next</button>
          </div>
        </div>
      )}

      {editing && (
        <InstitutionDialog
          institution={editing === "new" ? null : editing}
          options={options}
          onClose={() => setEditing(null)}
        />
      )}

      {hodFor && <HodDialog institution={hodFor} onClose={() => setHodFor(null)} />}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/* Add / edit an institution                                               */
/* ══════════════════════════════════════════════════════════════════════ */

function InstitutionDialog({
  institution, options, onClose,
}: { institution: QsInstitution | null; options: any; onClose: () => void }) {
  const [form, setForm] = useState<Record<string, any>>(() => ({
    name: institution?.name ?? "",
    acronym: institution?.acronym ?? "",
    state_id: institution?.state_id ?? "",
    type: institution?.type ?? "university",
    programmes: institution?.programmes ?? [],
    programmes_raw: institution?.programmes_raw ?? "",
    accreditation_status: institution?.accreditation_status ?? "unknown",
    accreditation_note: institution?.accreditation_note ?? "",
    hod_name: institution?.hod_name ?? "",
    hod_role: institution?.hod_role ?? "hod",
    hod_membership_no: institution?.hod_membership_no ?? "",
    hod_phone: institution?.hod_phone ?? "",
    hod_email: institution?.hod_email ?? "",
    contact_name: institution?.contact_name ?? "",
    contact_email: institution?.contact_email ?? "",
    contact_phone: institution?.contact_phone ?? "",
    other_information: institution?.other_information ?? "",
    is_active: institution?.is_active ?? true,
  }));
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [create, { isLoading: creating }] = useCreateQsInstitutionMutation();
  const [update, { isLoading: updating }] = useUpdateQsInstitutionMutation();
  const [remove, { isLoading: deleting }] = useDeleteQsInstitutionMutation();
  const busy = creating || updating;

  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const toggleProgramme = (code: string) =>
    set("programmes", form.programmes.includes(code)
      ? form.programmes.filter((c: string) => c !== code)
      : [...form.programmes, code]);

  const save = async () => {
    setError("");
    if (!form.name.trim()) return setError("The institution needs a name.");
    const body = { ...form, state_id: form.state_id || null };
    try {
      if (institution) await update({ id: institution.id, body }).unwrap();
      else await create(body).unwrap();
      onClose();
    } catch (e: any) {
      setError(e?.data?.message || "Could not save.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <div className="font-bold text-slate-800">
            {institution ? "Edit institution" : "Add institution"}
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-slate-100 text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Name</label>
              <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Acronym</label>
              <input className={inputCls} value={form.acronym} onChange={(e) => set("acronym", e.target.value)} />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">State</label>
              <select className={inputCls} value={String(form.state_id ?? "")} onChange={(e) => set("state_id", e.target.value)}>
                <option value="">—</option>
                {(options.states ?? []).map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Type</label>
              <select className={inputCls} value={form.type} onChange={(e) => set("type", e.target.value)}>
                {Object.entries(options.types ?? {}).map(([v, l]) => <option key={v} value={v}>{l as string}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Active</label>
              <select className={inputCls} value={form.is_active ? "1" : "0"} onChange={(e) => set("is_active", e.target.value === "1")}>
                <option value="1">On the register</option>
                <option value="0">Inactive</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1.5">Programmes</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(options.programmes ?? {}).map(([code, label]) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => toggleProgramme(code)}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-semibold ${
                    form.programmes.includes(code)
                      ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                      : "border-slate-200 text-slate-500 hover:bg-slate-50"
                  }`}
                >
                  {label as string}
                </button>
              ))}
            </div>
            <input
              className={`${inputCls} mt-2`}
              placeholder="As the register prints it, e.g. B.Sc, M.Sc, PGD, Ph.D"
              value={form.programmes_raw}
              onChange={(e) => set("programmes_raw", e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Accreditation</label>
              <select className={inputCls} value={form.accreditation_status} onChange={(e) => set("accreditation_status", e.target.value)}>
                {Object.entries(options.statuses ?? {}).map(([v, l]) => <option key={v} value={v}>{l as string}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">
                The register&rsquo;s own wording
              </label>
              <input className={inputCls} value={form.accreditation_note} onChange={(e) => set("accreditation_note", e.target.value)} />
            </div>
          </div>

          <div className="rounded-xl border p-4 space-y-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              Head of Department — as the register records them
            </div>
            <p className="text-[11px] text-slate-400 -mt-1">
              Matching them to a member account is done from the HOD button on their row, not here.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input className={inputCls} placeholder="Name" value={form.hod_name} onChange={(e) => set("hod_name", e.target.value)} />
              <select className={inputCls} value={form.hod_role} onChange={(e) => set("hod_role", e.target.value)}>
                {Object.entries(options.hod_roles ?? {}).map(([v, l]) => <option key={v} value={v}>{l as string}</option>)}
              </select>
              <input className={inputCls} placeholder="Membership no." value={form.hod_membership_no} onChange={(e) => set("hod_membership_no", e.target.value)} />
              <input className={inputCls} placeholder="Phone" value={form.hod_phone} onChange={(e) => set("hod_phone", e.target.value)} />
              <input className={`${inputCls} sm:col-span-2`} placeholder="Email" value={form.hod_email} onChange={(e) => set("hod_email", e.target.value)} />
            </div>
          </div>

          <div className="rounded-xl border p-4 space-y-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              Departmental contact
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input className={inputCls} placeholder="Name" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
              <input className={inputCls} placeholder="Phone" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} />
              <input className={`${inputCls} sm:col-span-2`} placeholder="Email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">Other information</label>
            <textarea rows={3} className={inputCls} value={form.other_information} onChange={(e) => set("other_information", e.target.value)} />
          </div>

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-1">
            {institution && (
              confirmDelete ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-red-600 font-semibold">Remove from the register?</span>
                  <button
                    onClick={async () => {
                      setError("");
                      try { await remove(institution.id).unwrap(); onClose(); }
                      catch (e: any) { setError(e?.data?.message || "Could not delete."); setConfirmDelete(false); }
                    }}
                    disabled={deleting}
                    className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-40"
                  >
                    {deleting ? "Removing…" : "Yes, remove"}
                  </button>
                  <button onClick={() => setConfirmDelete(false)} className="text-xs text-slate-500 underline">Cancel</button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" /> Remove
                </button>
              )
            )}
            <div className="flex-1" />
            <button onClick={onClose} className="px-4 py-2 rounded-lg border text-sm text-slate-600 hover:bg-slate-50">Cancel</button>
            <button
              onClick={save}
              disabled={busy}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-40"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/* Matching the Head of Department to a member                             */
/* ══════════════════════════════════════════════════════════════════════ */

/**
 * Finds the member behind the name on the register, and links them.
 *
 * It searches on what the register already holds, so the usual case is one
 * button press. The three boxes are for when those details are wrong and the
 * clerk has better ones from the department.
 *
 * It never picks for you. Where more than one member matches, all of them are
 * listed with what they matched on — being wrong here grants a stranger the
 * HOD role over somebody else's department.
 */
function HodDialog({ institution, onClose }: { institution: QsInstitution; onClose: () => void }) {
  const [membershipNo, setMembershipNo] = useState(institution.hod_membership_no ?? "");
  const [email, setEmail] = useState(institution.hod_email ?? "");
  const [phone, setPhone] = useState(institution.hod_phone ?? "");
  const [candidates, setCandidates] = useState<HodCandidate[] | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [done, setDone] = useState("");
  const [error, setError] = useState("");

  const [lookup, { isFetching }] = useLazyGetHodCandidatesQuery();
  const [link, { isLoading: linking }] = useLinkHodMutation();
  const [unlink, { isLoading: unlinking }] = useUnlinkHodMutation();

  const search = async () => {
    setError(""); setDone("");
    try {
      const res = await lookup({
        id: institution.id,
        membership_no: membershipNo.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
      }).unwrap();
      setCandidates(res.data);
      setNote(res.meta?.message ?? null);
    } catch (e: any) {
      setError(e?.data?.message || "Could not search the member register.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-xl max-h-[92vh] overflow-y-auto">
        <div className="border-b px-5 py-3 flex items-center justify-between">
          <div>
            <div className="font-bold text-slate-800">Head of Department</div>
            <div className="text-xs text-slate-500">{institution.name}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-slate-100 text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-lg border bg-slate-50 px-4 py-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
              On the register
            </div>
            <div className="text-sm font-semibold text-slate-800">
              {institution.hod_name || "No Head of Department recorded"}
            </div>
            <div className="text-xs text-slate-500">{institution.hod_role_label}</div>
          </div>

          {institution.hod_linked ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
                <BadgeCheck className="w-4 h-4" /> Matched to {institution.hod_user?.name}
              </div>
              <div className="text-xs text-emerald-800">{institution.hod_user?.email}</div>
              <p className="text-[11px] text-emerald-700">
                They hold the HOD role, in addition to whatever roles they already had.
              </p>
              <button
                onClick={async () => {
                  setError("");
                  try { await unlink(institution.id).unwrap(); setDone("Unlinked."); onClose(); }
                  catch (e: any) { setError(e?.data?.message || "Could not unlink."); }
                }}
                disabled={unlinking}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-300 bg-white text-sm text-emerald-800 hover:bg-emerald-50 disabled:opacity-40"
              >
                {unlinking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2Off className="w-4 h-4" />}
                Unlink
              </button>
            </div>
          ) : (
            <>
              <p className="text-xs text-slate-500 leading-relaxed">
                Search the member register by any one of these. Linking grants the member the
                <strong> HOD role</strong> &mdash; it is added to their existing roles, never
                replacing them.
              </p>

              <div className="grid grid-cols-1 gap-2">
                <input className={inputCls} placeholder="Membership number" value={membershipNo} onChange={(e) => setMembershipNo(e.target.value)} />
                <input className={inputCls} placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} />
                <input className={inputCls} placeholder="Phone number" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>

              <button
                onClick={search}
                disabled={isFetching}
                className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-40"
              >
                {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Find the member
              </button>

              {note && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">{note}</p>}

              {candidates && candidates.length > 0 && (
                <div className="space-y-2">
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    {candidates.length === 1 ? "One match" : `${candidates.length} matches — choose one`}
                  </div>
                  {candidates.map((c) => (
                    <div key={c.user_id} className="flex items-center gap-3 rounded-lg border px-3 py-2.5">
                      <GraduationCap className="w-5 h-5 text-slate-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-800 truncate">{c.name}</div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {c.membership_no ? `${c.membership_no} · ` : ""}{c.email}
                        </div>
                        <div className="text-[11px] text-slate-400">matched on {c.matched_on}</div>
                      </div>
                      <button
                        onClick={async () => {
                          setError("");
                          try { await link({ id: institution.id, user_id: c.user_id }).unwrap(); onClose(); }
                          catch (e: any) { setError(e?.data?.message || "Could not link."); }
                        }}
                        disabled={linking}
                        className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-40 shrink-0"
                      >
                        Link
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {error && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
            </div>
          )}
          {done && <div className="text-xs text-emerald-700">{done}</div>}
        </div>
      </div>
    </div>
  );
}
